import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'

import { DEVICE_MAP, SORT_MAP, VALID_API_SORTS, VALID_DEVICES } from '@/constants/search'
import {
  ContentTypeCode,
  ExploreRoomItem,
  Room,
  RoomSearchResultItem,
  SceneType,
  SearchAccount,
} from '@/services/cms'
import { FollowStatusData, postFollowStatus } from '@/services/community'
import { DeviceType, DeviceTypeWithAndroidIOS } from '@/types/configs'
import { DeviceValue, SortValue, WorldTypeValue } from '@/types/search'
import { SearchSort, WorldSort } from '@/types/store'

export type SearchResultTab = 'top' | 'games' | 'experiences' | 'videos' | 'creators'

const TAB_ORDER: SearchResultTab[] = ['top', 'games', 'experiences', 'videos', 'creators']

export const isValidApiSort = (value: string): value is WorldSort => {
  return VALID_API_SORTS.includes(value as WorldSort)
}

export const isValidDevice = (value: string): value is DeviceValue => {
  return VALID_DEVICES.includes(value as DeviceValue)
}

export const isValidTab = (tab: string): tab is SearchResultTab => {
  return TAB_ORDER.includes(tab as SearchResultTab)
}

export const getApiDevice = (value?: string | null): DeviceTypeWithAndroidIOS | undefined => {
  if (!value) return undefined
  return isValidDevice(value) ? DEVICE_MAP[value] : undefined
}

export const getApiSceneType = (type?: WorldTypeValue): SceneType => {
  if (type === 'video') return 'blendvision'
  if (type === 'world') return 'spoke,playcanvas,standalone'
  return 'all'
}

export const getApiType = (type?: WorldTypeValue | null): WorldTypeValue | undefined => {
  if (type === 'video') return 'video'
  if (type === 'world') return 'world'
  return undefined
}

export const getApiContentTypes = (type?: WorldTypeValue | null): ContentTypeCode[] | undefined => {
  if (type === 'game') return ['games']
  if (type === 'experience') return ['experiences']
  if (type === 'video') return ['videos']
  return undefined
}

export const getSearchContentTypes = (contentType: ContentTypeCode): ContentTypeCode[] => {
  return contentType === 'experiences' ? ['experiences', 'templates'] : [contentType]
}

export const isGamesOnlyContentTypes = (contentTypes?: ContentTypeCode[]): boolean => {
  if (!contentTypes?.length) return false
  return contentTypes.every((type) => type === 'games')
}

export const getApiSort = (sort?: string | null, isSearchPage?: boolean): SearchSort => {
  const defaultSort = 'recommend'
  const apiSort = SORT_MAP[sort as SortValue]

  return sort ? apiSort || defaultSort : defaultSort
}

const REVERSE_DEVICE_MAP: Partial<Record<DeviceTypeWithAndroidIOS, DeviceValue>> = {
  desktop: 'desktop',
  hmd: 'vr',
  ios: 'ios',
  android: 'android',
}

export const getDeviceValue = (apiDevice: DeviceTypeWithAndroidIOS): DeviceValue | undefined => {
  return REVERSE_DEVICE_MAP[apiDevice]
}

export const convertToDeviceType = (
  apiDevice: DeviceTypeWithAndroidIOS | undefined,
): DeviceType => {
  if (!apiDevice) return 'desktop'
  if (apiDevice === 'android' || apiDevice === 'ios') return 'mobile'
  if (apiDevice === 'hmd') return 'hmd'
  return 'desktop'
}

export const createSuggestionWorlds = (keywords: string[], prefix: string = 'keyword') =>
  keywords.map((keyword, index) => ({
    id: `${prefix}-${index}`,
    title: keyword,
  }))

export const adaptDeviceType = (
  deviceType: DeviceType,
  deviceTypeWithAndroidIOS: DeviceTypeWithAndroidIOS,
): DeviceTypeWithAndroidIOS => {
  if (!deviceType) return deviceTypeWithAndroidIOS
  if (deviceType !== 'mobile') return deviceType

  if (deviceTypeWithAndroidIOS === 'desktop' || deviceTypeWithAndroidIOS === 'hmd') {
    return 'android'
  }

  return deviceTypeWithAndroidIOS
}

// 🥷🏼 Replace URL with query string and preserve #explore-viverse hash
export function replaceUrlWithQueryString(
  router: AppRouterInstance,
  pathname: string,
  queryString: string,
  hash: string | null,
  autoScroll = true,
) {
  const hashToPreserve =
    hash?.split('#').find(Boolean) === 'explore-viverse' ? '#explore-viverse' : ''

  router.replace(
    queryString ? `${pathname}?${queryString}${hashToPreserve}` : `${pathname}${hashToPreserve}`,
    { scroll: autoScroll },
  )
}

export async function fetchFollowStatusForAccounts(
  accounts: SearchAccount[] = [],
  htcToken: string | undefined,
): Promise<FollowStatusData[]> {
  if (accounts.length === 0) {
    return []
  }

  const preferredUsernames = [...new Set(accounts.map((account) => account.community_user_id))]

  if (preferredUsernames.length === 0) {
    return []
  }

  try {
    return (await postFollowStatus(htcToken, preferredUsernames))?.data ?? []
  } catch (error) {
    console.error('Failed to fetch follow status:', error)
    return []
  }
}

export const mapRoomResults = (rooms: RoomSearchResultItem[]): Room[] =>
  rooms.map(
    (room) =>
      ({
        ...(room as Room),
        owner: room.owner_name
          ? {
              ...room.owner,
              display_name: room.owner_name,
              community_user_id: room.owner_community_user_id,
              viverse_pro: room.owner_viverse_pro,
              verified_creator: room.owner_verified_creator,
            }
          : room.owner,
      }) as Room,
  )

type RoomPreviewOwnerSource = {
  display_name?: string
  owner_name?: string
  community_user_id?: string
  owner_community_user_id?: string
  owner_viverse_pro?: boolean
  owner_verified_creator?: boolean
}

export const createRoomPreviewOwner = (item: RoomPreviewOwnerSource): Room['owner'] =>
  ({
    id: item.community_user_id ?? item.owner_community_user_id ?? '',
    display_name: item.display_name ?? item.owner_name ?? '',
    community_user_id: item.community_user_id ?? item.owner_community_user_id,
    viverse_pro: item.owner_viverse_pro,
    verified_creator: item.owner_verified_creator,
    snapshot_url: '',
    head_icon_url: '',
  }) as Room['owner']

export const mapExploreRoomItemToRoom = (item: ExploreRoomItem): Partial<Room> => {
  const { tags, ...rest } = item

  return {
    ...rest,
    owner: createRoomPreviewOwner(item),
    tags: tags?.map((tag) => tag.name),
    hubs_url: `/${item.hub_sid}`,
  }
}
